import {
  Button,
  ButtonSize,
  ButtonVariant,
  DatePicker,
  Divider,
  FileList,
  InputFileDropdown,
  Panel,
  Row,
  RowSize,
  TableRow,
  toast,
  ValidationWrapper,
} from "@/components";
import { FileProps } from "@/components/file-list/types";
import { UserInputValue } from "@/components/input-file/types";
import { InputValidated } from "@/components/input-validated";
import {
  CICPA_TOAST_ID,
  COMPANY_NAME_AR_TOAST_ID,
  CONTRACTORS_LICENCE_DATE_FORMAT,
  DocTypeIds,
  NO_VALUE,
  TOAST_DEFAULT_DURATION,
} from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { useUserData } from "@/pages/application/hooks";
import { useOperationsWithAttachments } from "@/pages/contractor-profile/hooks/use-operations-with-attachments";
import { SubcontractorField } from "@/pages/contractor-profile/subcontractor/edit/types";
import { requestSaveContractorsForm } from "@/services/api/contractor";
import { selectUserData, useBoundStore } from "@/services/stores";
import { checkIfStringAndDash } from "@/utils/check-if-string-and-dash";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { getReasonPhrase, StatusCodes } from "http-status-codes";
import { compact, size } from "lodash";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ContractorFormSchema } from "./schema";
import styles from "./styles.scss";
import {
  ContractorField,
  ContractorFieldLabel,
  ContractorFieldSubLabel,
} from "./types";

export const Contractors: FC = () => {
  const { account } = useContext(AuthContext);
  const { user } = useBoundStore(selectUserData);
  const { onGetUser } = useUserData(false);
  const contractor = user?.Contractor;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolver = zodResolver(ContractorFormSchema);

  const {
    clearErrors,
    setValue,
    getValues,
    formState: { errors, isValid },
    handleSubmit,
    trigger,
  } = useForm({ resolver });

  const handleDataChange = useCallback(
    (value: UserInputValue, dropdownId: ContractorField) => {
      clearErrors(dropdownId);
      setValue(dropdownId, value, { shouldValidate: true });
    },
    [clearErrors, setValue],
  );

  useEffect(() => {
    if (contractor) {
      const {
        ContractorCicpaId,
        TradeLicenseExpiryDate,
        TradeLicenseDocument,
        CompanyNameAr,
      } = contractor;

      if (ContractorCicpaId) {
        handleDataChange(
          ContractorCicpaId.toString(),
          ContractorField.CICPA_ID,
        );
      }

      if (TradeLicenseDocument) {
        handleDataChange(
          TradeLicenseDocument?.FileBase64,
          ContractorField.TRADE_LICENSE,
        );

        handleDataChange(
          TradeLicenseDocument?.DocTypeId || "",
          ContractorField.TRADE_LICENSE_DOCTYPE_ID,
        );
      }

      handleDataChange(
        (TradeLicenseDocument?.FileName || TradeLicenseDocument?.DocNameEn) ??
          NO_VALUE,
        ContractorField.TRADE_LICENSE_FILE_NAME,
      );
      handleDataChange(
        TradeLicenseDocument?.Format || NO_VALUE,
        ContractorField.TRADE_LICENSE_FILE_FORMAT,
      );

      if (TradeLicenseExpiryDate) {
        handleDataChange(
          new Date(TradeLicenseExpiryDate),
          ContractorField.TRADE_LICENSE_EXPIRY,
        );
      }

      if (CompanyNameAr) {
        handleDataChange(CompanyNameAr, ContractorField.CONTRACTOR_ARABIC_NAME);
      }
    }
  }, [contractor, handleDataChange]);

  const { onUploadDocument, onRemoveDocument } = useOperationsWithAttachments({
    getValues,
    setValue,
    clearErrors,
    trigger,
  });

  const handleRemoveFile = (): void => {
    onRemoveDocument(ContractorField.TRADE_LICENSE);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);

    const toastId = toast.info("Submitting contractor...", {
      duration: TOAST_DEFAULT_DURATION * 5,
    });

    const formData = getValues();

    try {
      const controller = new AbortController();

      if (contractor?.AdnocId) {
        const payload = {
          AdnocId: `${contractor?.AdnocId}`,
          Email: contractor?.Email,
          CicpaId: `${formData[ContractorField.CICPA_ID]}`,
          ContractorArabicName: `${formData[ContractorField.CONTRACTOR_ARABIC_NAME]}`,
          TradeLicenseDocument: {
            DocTypeId: DocTypeIds.Contractor,
            FileName: formData[ContractorField.TRADE_LICENSE_FILE_NAME],
            DocNameEn: formData[ContractorField.TRADE_LICENSE_FILE_NAME],
            Format: formData[ContractorField.TRADE_LICENSE_FILE_FORMAT],
            FileBase64: formData[ContractorField.TRADE_LICENSE],
            TemplateTitle: null,
            TemplatePath: null,
          },
          TradeLicenseExpiryDate: format(
            formData[ContractorField.TRADE_LICENSE_EXPIRY],
            CONTRACTORS_LICENCE_DATE_FORMAT,
          ),
        };

        await requestSaveContractorsForm(
          {
            email: account?.username ?? "",
          },
          payload,
          { signal: controller.signal },
        );

        await onGetUser();

        toast.success("Contractor form is saved", {
          id: toastId,
          duration: TOAST_DEFAULT_DURATION,
        });

        const cicpaToast = window.sessionStorage.getItem(CICPA_TOAST_ID);

        if (cicpaToast) {
          toast.remove(cicpaToast);
          window.sessionStorage.removeItem(CICPA_TOAST_ID);
        }

        const companyNameArToast = window.sessionStorage.getItem(
          COMPANY_NAME_AR_TOAST_ID,
        );

        if (companyNameArToast) {
          toast.remove(companyNameArToast);
          window.sessionStorage.removeItem(COMPANY_NAME_AR_TOAST_ID);
        }

        setIsSubmitting(false);
      }
    } catch (error) {
      const msg =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        error?.message ?? getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

      toast.error(msg, {
        id: toastId,
        duration: TOAST_DEFAULT_DURATION,
      });
      setIsSubmitting(false);
    }
  };

  // TODO Unify attachment, attachmentName, attachmentFormat with request attachments
  const attachment: FileProps[] = getValues([ContractorField.TRADE_LICENSE]);

  const attachmentName: string[] = getValues([
    ContractorField.TRADE_LICENSE_FILE_NAME,
  ]);

  const attachmentFormat: string[] = getValues([
    ContractorField.TRADE_LICENSE_FILE_FORMAT,
  ]);

  const attachmentDocTypeId: string = getValues("DocTypeId");

  return (
    <div className={styles.pageWrapper}>
      <Row size={RowSize.Small}>
        <Panel header="Contract Details">
          <TableRow label="Company ID" value={contractor?.CompanyNo} />
          <TableRow
            label="Arabic Name"
            className={styles.arabicNameContainer}
            valueClassName={styles.arabicNameValueContainer}
            value={
              <InputValidated
                subLabel={ContractorFieldSubLabel.ARABIC_NAME}
                placeholder="Type here"
                type="text"
                value={getValues([ContractorField.CONTRACTOR_ARABIC_NAME])}
                onChange={e => {
                  handleDataChange(
                    e.target.value,
                    ContractorField.CONTRACTOR_ARABIC_NAME,
                  );
                }}
                errorMessage={
                  errors[ContractorField.CONTRACTOR_ARABIC_NAME]
                    ?.message as string
                }
                mandatory
              />
            }
            mandatory
          />
          <TableRow label="English Name" value={contractor?.CompanyNameEn} />
          <TableRow label="Unified ID" value={contractor?.AdnocId} />
          <TableRow
            label="Trade License Number"
            value={contractor?.AdcciMembershipNo}
          />
          <TableRow label="Telephone No" value={contractor?.PhoneNumber} />
        </Panel>
      </Row>

      <form>
        <Row size={RowSize.Small}>
          <InputValidated
            label={ContractorFieldLabel.CICPA_ID}
            placeholder="Type here"
            type="text"
            value={getValues([ContractorField.CICPA_ID])}
            onChange={e => {
              const isValid = checkIfStringAndDash(e.target.value);

              if (isValid) {
                handleDataChange(e.target.value, ContractorField.CICPA_ID);
              }
            }}
            errorMessage={errors[ContractorField.CICPA_ID]?.message as string}
            mandatory
          />
        </Row>

        <Row size={RowSize.Small}>
          <ValidationWrapper
            errorMessage={
              errors[ContractorField.TRADE_LICENSE_EXPIRY]?.message as string
            }
          >
            <DatePicker
              wrapperClassName={styles.datePicker}
              labelClassName={styles.datePickerLabel}
              mode="single"
              inputLabel={ContractorFieldLabel.TRADE_LICENSE_EXPIRY}
              inputPlaceholder="dd/mm/yyyy"
              selected={getValues(ContractorField.TRADE_LICENSE_EXPIRY)}
              onSelectedDateChange={date => {
                handleDataChange(date, ContractorField.TRADE_LICENSE_EXPIRY);
              }}
              floatingOptions={{
                strategy: "fixed",
              }}
              defaultMonth={
                getValues(ContractorField.TRADE_LICENSE_EXPIRY) || new Date()
              }
              mandatory
            />
          </ValidationWrapper>
        </Row>

        <Row size={RowSize.Small}>
          <InputFileDropdown
            requiredOnly={contractor?.TradeLicenseDocument?.Format}
            label={ContractorFieldLabel.TRADE_LICENSE}
            placeholder="Upload Trade Licence"
            mandatory
            onChange={e => onUploadDocument(e, ContractorField.TRADE_LICENSE)}
            errorMessage={
              errors[SubcontractorField.TRADE_LICENSE]?.message as string
            }
          />
        </Row>
      </form>

      {size(compact([attachment])) && (
        <FileList
          file={attachment}
          fileName={attachmentName[0]}
          fileFormat={attachmentFormat[0]}
          fileId={attachmentDocTypeId}
          onRemove={handleRemoveFile}
          fileItemNameClassName={styles.fileItemName}
        />
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <Row size={RowSize.Small} className={styles.flexEnd}>
        <Button
          variant={ButtonVariant.Primary}
          size={ButtonSize.Small}
          className={styles.actionButton}
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isSubmitting}
        >
          Save
        </Button>
      </Row>
    </div>
  );
};
